import { Map as ImmutableMap } from 'immutable';
import * as ActionTypes from '../../../actions/ActionTypes';
import CategoryEntity from '../../../records/CategoryEntityRecord';
const getCategoriesFromCategoriesOrganization = payload => {
  const allCategories = {};
  for (const category of payload.categories) {
    allCategories[category.id] = CategoryEntity.from(category);
  }
  return ImmutableMap(allCategories);
};
const updateCategoriesFromTranslationSuccess = (state, response) => {
  const {
    categories
  } = response;
  return state.withMutations(newState => {
    categories.forEach(updatedCategory => {
      const id = updatedCategory.knowledgeCategoryId.toString();
      const category = newState.get(id);
      if (!category) {
        return; // Don't try to update state if we don't have the category locally
      }
      updatedCategory.nameTranslations.forEach(updatedTranslation => {
        const translationLanguage = updatedTranslation.language;
        const translationIndex = category.translations.findIndex(t => t.language === translationLanguage);
        if (translationIndex >= 0) {
          newState.update(id, c => c.setIn(['translations', translationIndex], updatedTranslation));
        } else {
          newState.updateIn([id, 'translations'], translations => translations.push(updatedTranslation));
        }
        if (!newState.getIn([id, 'translatedToLanguages']).includes(translationLanguage)) {
          newState.updateIn([id, 'translatedToLanguages'], languages => languages.concat(translationLanguage));
        }
      });
    });
  });
};
const removeSubcategory = (state, subcategoryId) => {
  const parentId = state.findKey(cat => cat.subcategories.includes(subcategoryId));
  return state.updateIn([parentId, 'subcategories'], subcategoryIds => subcategoryIds.delete(subcategoryIds.indexOf(subcategoryId)));
};
const insertSubcategory = (state, insertSubcategoryId, targetSubcategoryId, position) => {
  const targetParentId = state.findKey(cat => cat.subcategories.includes(targetSubcategoryId));
  return state.updateIn([targetParentId, 'subcategories'], subcategoryIds => subcategoryIds.insert(position === 'before' ? subcategoryIds.indexOf(targetSubcategoryId) : subcategoryIds.indexOf(targetSubcategoryId) + 1, insertSubcategoryId));
};
const insertSubcategoryAtParentIndex = (state, subcategoryId, parentCategoryId, index) => state.updateIn([parentCategoryId, 'subcategories'], subcategoryIds => subcategoryIds.insert(index, subcategoryId));
const insertArticleAtParentIndex = (state, articleId, parentCategoryId, index) => state.updateIn([parentCategoryId, 'articles'], articleIds => articleIds.insert(index, articleId));
const removeArticle = (state, articleId) => {
  let nextState = state;
  const parentId = nextState.findKey(cat => cat.articles.includes(articleId));
  if (parentId) {
    nextState = nextState.updateIn([parentId, 'articles'], articleIds => articleIds.delete(articleIds.indexOf(articleId)));
  }
  return nextState;
};
const insertArticleAtPosition = (state, catId, insertArticleId, targetArticleId, relativePosition) => {
  return state.updateIn([catId, 'articles'], articleIds => articleIds.insert(relativePosition === 'before' ? articleIds.indexOf(targetArticleId) : articleIds.indexOf(targetArticleId) + 1, insertArticleId));
};
const INITIAL_STATE = ImmutableMap();
export default function categoriesReducer(state = INITIAL_STATE, action) {
  switch (action.type) {
    case ActionTypes.CLEAR_ORGANIZE_STATE:
      return INITIAL_STATE;
    case ActionTypes.UPDATE_CATEGORY_TRANSLATIONS_SUCCESS:
      return updateCategoriesFromTranslationSuccess(state, action.payload);
    case ActionTypes.FETCH_TRANSLATED_CATEGORY_ORGANIZATION_BUFFER_SUCCESS:
    case ActionTypes.FETCH_CATEGORY_ORGANIZATION_BUFFER_SUCCESS:
    case ActionTypes.FETCH_TRANSLATED_CATEGORY_ORGANIZATION_SUCCESS:
      return getCategoriesFromCategoriesOrganization(action.payload);
    case ActionTypes.DROP_SUBCATEGORY_ON_SUBCATEGORY:
      {
        const {
          dragSubcategoryId,
          dropSubcategoryId,
          dropPosition
        } = action.payload;
        const nextState = removeSubcategory(state, dragSubcategoryId);
        return insertSubcategory(nextState, dragSubcategoryId, dropSubcategoryId, dropPosition);
      }
    case ActionTypes.UPDATE_SUBCATEGORY_ORDERING_ERROR:
      {
        const {
          revertInfo: {
            categoryId,
            originalParentId,
            originalParentIndex
          }
        } = action.payload;
        const nextState = removeSubcategory(state, categoryId);
        return insertSubcategoryAtParentIndex(nextState, categoryId, originalParentId, originalParentIndex);
      }
    case ActionTypes.UPDATE_ARTICLE_ORDERING_ERROR:
      {
        const {
          revertInfo: {
            articleId,
            originalParentId,
            originalParentIndex
          }
        } = action.payload;
        let nextState = removeArticle(state, articleId);
        const originalParentIsCategory = nextState.has(originalParentId);
        if (originalParentIsCategory) {
          nextState = insertArticleAtParentIndex(nextState, articleId, originalParentId, originalParentIndex);
        }
        return nextState;
      }
    case ActionTypes.DROP_ARTICLE_ON_ARTICLE:
      {
        const {
          dragArticleId,
          dropArticleId,
          dropPosition
        } = action.payload;
        let nextState = removeArticle(state, dragArticleId);
        const categoryContainingTargetArticle = nextState.findKey(cat => cat.articles.includes(dropArticleId));
        if (categoryContainingTargetArticle) {
          nextState = insertArticleAtPosition(nextState, categoryContainingTargetArticle, dragArticleId, dropArticleId, dropPosition);
        }
        return nextState;
      }
    case ActionTypes.DROP_ARTICLE_ON_SUBCATEGORY:
      {
        const {
          dragArticleId
        } = action.payload;
        return removeArticle(state, dragArticleId);
      }
    case ActionTypes.DROP_ARTICLE_ON_CATEGORY:
      {
        const {
          dragArticleId,
          dropCategoryId
        } = action.payload;
        const nextState = removeArticle(state, dragArticleId);
        return nextState.updateIn([dropCategoryId, 'articles'], articleIds => articleIds.push(dragArticleId));
      }
    default:
      return state;
  }
}